import React, { createContext, useEffect, useState } from "react";
import * as notify from "shared/notify";
import eventEmitter from "shared/events";
import { INITIAL_STATE, getUomWeightList, uom, DEFAULT_POSITION_VALUE } from "components/measuring/lib/constants";
import { differenceWith, isEqual } from "lodash";
import { setDefaultPosition, validateMeasurement } from "components/measuring/lib/helpers";
import { usePositions } from "components/measuring/hooks/api/use-positions";
import { isEmpty } from "lodash";
import { useSaveMeasurements } from "../measurements/hooks/use-save-measurements";
import { useUpdateMeasurements } from "../measurements/hooks/use-update-measurements";
import { useDeleteSingleMeasurement } from "../measurements/hooks/use-delete-single-measurement";
import { useDeleteMeasurementWipKeys } from "../measurements/hooks/use-delete-measurement-wipkeys";
import { useRemovePosition } from "../measurements/hooks/use-remove-position";

export const MeasurementsContext = createContext({});

export const MeasurementsProvider = (props) => {
    const [measurementState, setMeasurementState] = useState(INITIAL_STATE);
    const [selectedUpcs, setSelectedUpcs] = useState([]);
    const [singleHighlightedCodingWipKey, setSingleHighlightedCodingWipKey] = useState("");
    const [singleHighlightedMeasuringWipKey, setSingleHighlightedMeasuringWipKey] = useState("");
    const { data: positionListData, isSuccess } = usePositions();
    const { mutate: saveNewMeasurements } = useSaveMeasurements();
    const { mutate: updateMeasurements } = useUpdateMeasurements();
    const { mutate: deleteSingleMeasurement } = useDeleteSingleMeasurement();
    const { mutate: deleteMultiMeasurements } = useDeleteMeasurementWipKeys();
    const { mutate: deletePosition } = useRemovePosition();

    useEffect(() => {
        if (positionListData && isSuccess) {
            return setMeasurementState({
                ...measurementState,
                fullPositionList: positionListData,
                positionList: setDefaultPosition(positionListData, measurementState.measurements),
            });
        }
    }, [positionListData, isSuccess]);

    const updateSelectedUpcs = (upc, isRemoved) => {
        if (isRemoved) {
            return setSelectedUpcs(differenceWith(selectedUpcs, [upc], isEqual));
        }

        return setSelectedUpcs([...selectedUpcs, upc]);
    };

    const initializeMeasurementsContainer = (measurementsFromPhotography) => {
        setMeasurementState((current) => {
            const initialMeasurements = measurementsFromPhotography ? measurementsFromPhotography : current.measurements ? current.measurements : [];

            return {
                ...current,
                measurements: initialMeasurements,
            };
        });
    };

    const updatePosition = (position) => {
        return setMeasurementState((current) => ({
            ...current,
            currentPosition: position,
        }));
    };

    const addMeasurement = async (currentState, type, position) => {
        const newMeasurement = {
            positionValue: position.text.replace(" ", "_"),
            position: position.name,
            fullPosition: position,
            height: { value: 0, uom: uom[type][0].value },
            width: { value: 0, uom: uom[type][0].value },
            weight: { value: 0, uom: getUomWeightList(position.value)[type][0].value },
            depth: { value: 0, uom: uom[type][0].value },
            key: null,
            type: type,
        };

        const measurement = saveMeasurement(currentState, newMeasurement, true);

        return measurement;
    };

    const saveMeasurement = (currentState, measurement, allowZeroMeasurements, sourcePage) => {
        // Validate measurement
        const invalidMeasurements = validateMeasurement(measurement, allowZeroMeasurements);

        if (invalidMeasurements.length > 0) {
            const existingInvalid = [...currentState.invalidMeasurements];
            const foundInvalid = existingInvalid.find((m) => m.key === measurement.key);

            if (foundInvalid) {
                foundInvalid.errors = invalidMeasurements;
                foundInvalid.message = `<br />${measurement.position} ${measurement.type}: ${invalidMeasurements.join(", ")}`;
            } else {
                existingInvalid.push({
                    errors: invalidMeasurements,
                    key: measurement.key,
                    message: `<br />${measurement.position} ${measurement.type}: ${invalidMeasurements.join(", ")}`,
                });
            }

            const newMeasurements = [...currentState.measurements];
            const measurementIndex = newMeasurements.findIndex((m) => measurement.key.includes(m.key[0]));

            if (measurementIndex >= 0) {
                newMeasurements[measurementIndex] = measurement;
            }

            setMeasurementState((current) => ({
                ...current,
                invalidMeasurements: existingInvalid,
                measurements: current.measurements.map((currentMeasurement, index) => {
                    if (index === measurementIndex) {
                        return measurement;
                    }

                    return currentMeasurement;
                }),
            }));

            return false;
        }

        if (!measurement.key) {
            const selectedUpcsWipKeys = !isEmpty(selectedUpcs) && selectedUpcs.map((upc) => upc.measuringWipKey);

            const wipKeys = selectedUpcsWipKeys ? selectedUpcsWipKeys : currentState.measuringWipKey ? [currentState.measuringWipKey] : [];

            return saveNewMeasurements(
                { measurement, wipKeys },
                {
                    onSuccess: (response) => {
                        if (!response || isEmpty(response)) {
                            return;
                        }

                        return setMeasurementState((current) => ({
                            ...current,
                            measurements: [...current.measurements, response],
                        }));
                    },
                }
            );
        } else {
            return updateMeasurements(
                { measurement, sourcePage },
                {
                    onSuccess: (response) => {
                        if (!response) {
                            return;
                        }

                        const newMeasurements = [...currentState.measurements];
                        const measurementIndex = newMeasurements.findIndex((m) => measurement.key.includes(m.key[0]));

                        if (measurementIndex >= 0) {
                            newMeasurements[measurementIndex] = measurement;
                        }

                        return setMeasurementState((current) => ({
                            ...current,
                            measurements: current.measurements.map((currentMeasurement, index) => {
                                if (index === measurementIndex) {
                                    return measurement;
                                }

                                return currentMeasurement;
                            }),
                            invalidMeasurements: [
                                ...current.invalidMeasurements.filter((invalid) => invalid.key.every((k) => !measurement.key.includes(k))),
                            ],
                        }));
                    },
                }
            );
        }
    };

    const setInvalidMeasurements = (errors) => {
        return setMeasurementState((current) => ({
            ...current,
            invalidMeasurements: errors,
        }));
    };

    const addPosition = (position) => {
        return setMeasurementState((current) => {
            const isAvailable = current.positionList.available.some((p) => p.id === position.id);

            if (!isAvailable) {
                return;
            }

            const newActive = [...current.positionList.active, position];
            const newAvailable = current.positionList.available.filter((p) => p.id !== position.id);

            return {
                ...current,
                positionList: {
                    active: newActive,
                    available: newAvailable,
                    all: [...newActive, ...newAvailable],
                },
            };
        });
    };

    const findMeasurementsToDelete = (currentState, keys) => {
        const { measurements } = currentState;

        keys.forEach((key) => {
            const measurementToDelete = measurements.find((m) => m.key.includes(key));

            if (measurementToDelete) {
                const newMeasurements = measurements.filter((m) => !m.key.includes(key));

                return setMeasurementState((current) => ({
                    ...current,
                    measurements: newMeasurements,
                }));
            }
        });
    };

    const deleteSingleMeasurementWipKey = (currentState, keys) => {
        return deleteSingleMeasurement(
            { measuringWipKey: currentState.measuringWipKey, keys },
            {
                onSuccess: () => {
                    eventEmitter.emit("REFRESH_TASK_STATUS");

                    return findMeasurementsToDelete(currentState, keys);
                },
            }
        );
    };

    const deleteMeasurementsWipKeys = async (currentState, keys) => {
        return deleteMultiMeasurements(
            { keys, selectedUpcs },
            {
                onSuccess: () => {
                    eventEmitter.emit("REFRESH_TASK_STATUS");

                    return findMeasurementsToDelete(currentState, keys);
                },
            }
        );
    };

    const removePosition = async (currentState, position, measurements) => {
        if (position.value === DEFAULT_POSITION_VALUE) {
            return notify.error(`Cannot delete ${position.text} position`);
        }

        const measurementsToDelete = measurements.filter((m) => m.fullPosition.id === position.id);

        return deletePosition(
            {
                measuringWipKey: currentState.measuringWipKey,
                measurements: measurementsToDelete,
            },
            {
                onSuccess: () => {
                    const isActive = currentState.positionList.active.some((p) => p.id === position.id);

                    if (!isActive) {
                        return;
                    }

                    const newActive = currentState.positionList.active.filter((p) => p.id !== position.id);

                    const newAvailable = [...currentState.positionList.available, position];

                    return setMeasurementState((current) => ({
                        ...current,
                        positionList: {
                            active: newActive,
                            available: newAvailable,
                        },
                    }));
                },
            }
        );
    };

    return (
        <MeasurementsContext.Provider
            value={{
                selectedUpcs,
                setSelectedUpcs,
                updateSelectedUpcs,
                singleHighlightedCodingWipKey,
                setSingleHighlightedCodingWipKey,
                singleHighlightedMeasuringWipKey,
                setSingleHighlightedMeasuringWipKey,
                initializeMeasurementsContainer,
                setMeasurementState,
                measurementState,
                updatePosition,
                saveMeasurement,
                addMeasurement,
                setInvalidMeasurements,
                addPosition,
                deleteSingleMeasurementWipKey,
                deleteMeasurementsWipKeys,
                removePosition,
            }}
        >
            {props.children}
        </MeasurementsContext.Provider>
    );
};
